import { agentsData } from "Stores/agentsData";

// The events an agent can react to, and the filters each one offers. The list comes from the
// server (GET /cm/agents/trigger-types) so the picker, the generator prompt and the matcher can't
// drift apart; FALLBACK is what to show while that request is in flight or if it fails.
export const FALLBACK_EVENTS = [
  { value: "EMAIL_REPLIED", label: "Contact replies to an email", filters: [] },
  { value: "LINKEDIN_REPLIED", label: "Contact replies on LinkedIn", filters: [] },
  { value: "LI_CONNECTION_REQUEST_ACCEPTED", label: "LinkedIn connection request accepted", filters: [] },
  { value: "EMAIL_OPENED", label: "Contact opens an email", filters: [] },
  { value: "EMAIL_CLICKED", label: "Contact clicks a link", filters: [] },
  { value: "EMAIL_DELIVERED", label: "An email is delivered", filters: [] },
  { value: "EMAIL_BOUNCED", label: "An email bounces", filters: [] },
  { value: "CONTACT_CREATED", label: "A new contact is created", filters: [] },
  { value: "CONTACT_STAGE_CHANGED", label: "A contact’s stage changes", filters: [] },
  { value: "CONTACT_UPDATED", label: "A contact is updated", filters: [] },
  { value: "ADDED_TO_SEQUENCE", label: "A contact is added to a sequence", filters: [] },
  { value: "SEQUENCE_COMPLETED", label: "A contact finishes a sequence", filters: [] },
  { value: "COMPLETED_ON_ALL_SEQUENCES", label: "A contact finishes all its sequences", filters: [] },
  { value: "CALL_LOGGED", label: "A call is logged", filters: [] },
  { value: "CONTACT_OPTEDOUT", label: "A contact opts out", filters: [] }
];

/** The catalog to render with: the server's list once it's loaded. */
export const triggerEvents = () =>
  agentsData.triggerTypes && agentsData.triggerTypes.length ? agentsData.triggerTypes : FALLBACK_EVENTS;

export const eventOptions = () => triggerEvents().map(e => ({ value: e.value, label: e.label }));

export const eventLabel = value => {
  const found = triggerEvents().find(e => e.value === value);
  return found ? found.label : value;
};

/** The filters this event offers, e.g. "Only when the new stage is" for a stage change. */
export const eventFilters = value => {
  const found = triggerEvents().find(e => e.value === value);
  return (found && found.filters) || [];
};

/** A filter's choices: the team's own stages, fields or sequences, or a fixed set from the catalog. */
export const filterOptions = filter => {
  if (filter.options) return filter.options.slice();
  if (filter.valueType === "contactStage") return agentsData.contactStages.slice();
  if (filter.valueType === "sequence") return agentsData.sequencesLite.slice();
  return agentsData.contactFields.slice();
};

/** A picked value as the user knows it — a sequence is stored by id but shown by name. */
export const filterValueLabel = (filter, value) => {
  const found = filterOptions(filter).find(o => o.value === value);
  return found ? found.label : value;
};

/** Keep only the filters that belong to this event, so switching event doesn't carry stale ones. */
export const filterConfigFor = (value, config) => {
  const out = {};
  eventFilters(value).forEach(f => {
    const picked = (config || {})[f.key];
    if (Array.isArray(picked) && picked.length) out[f.key] = picked;
  });
  return out;
};

/** "Only when the new stage is Working" — the filters as one readable line, or "" when none. */
export const filterSummary = (value, config) => {
  const parts = eventFilters(value)
    .map(f => {
      const picked = (config || {})[f.key];
      if (!Array.isArray(picked) || !picked.length) return "";
      return `${f.label.toLowerCase()} ${picked.map(v => filterValueLabel(f, v)).join(", ")}`;
    })
    .filter(Boolean);
  return parts.join("; ");
};

/** "It runs …" for the Turn on hint, from the trigger kind and its Runs summary. */
export const runsSentence = (kind, summary) => {
  if (kind === "schedule" || kind === "signal") return `It runs ${String(summary || "").replace(/^./, c => c.toLowerCase())}`;
  if (kind === "event") return `It runs each time this happens: ${summary}`;
  return "It runs when you start it";
};
